import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter } from 'react-router-dom';

import { EMTPY_LIST_ITEMS } from '@common/constants';
import {
	CommandsManager,
	ExtensionManager,
	HotkeysManager,
	ServiceProvidersManager,
	ServicesManager,
	SessionAuthenticationProvider,
	SystemContextProvider,
	ViewportRefsProvider,
} from '@core';
import isMobileDevice from '@core/utils/isMobileDevice';
import i18n from '@i18n';
import {
	DialogProvider,
	DicomPrintProvider,
	Modal,
	ModalProvider,
	SnackbarProvider,
	ThemeWrapper,
	ViewportDialogProvider,
	ViewportGridProvider,
} from '@ui';
import appInit from './appInit';
import { useWakeLock } from './hooks';
import createRoutes from './routes';
import Compose from './routes/Mode/Compose';
import { AppConfigProvider } from './state';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
});

const URL_PARAMS = new URLSearchParams(window.location.search);
const isMobileModeSupport = URL_PARAMS.get('mobile_support') === 'true' || URL_PARAMS.get('mobile_support') === '1';

if (process.env.REACT_APP_SENTRY_DSN) {
	console.log('init sentry');
	Sentry.init({
		dsn: process.env.REACT_APP_SENTRY_DSN,
		integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
		// Tracing
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: [process.env.PUBLIC_URL],
		// Session Replay
		replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
	});
}

let commandsManager: CommandsManager,
	extensionManager: ExtensionManager,
	servicesManager: ServicesManager,
	serviceProvidersManager: ServiceProvidersManager,
	hotkeysManager: HotkeysManager;

function App({
	config = {
		routerBaseName: '/',
		showLoadingIndicator: true,
		showStudyList: true,
		oidc: [],
		extensions: [],
	},
	defaultExtensions = EMTPY_LIST_ITEMS,
	defaultModes = EMTPY_LIST_ITEMS,
}) {
	useWakeLock();
	const isTabletOrMobile = isMobileDevice() && isMobileModeSupport;
	console.log('isTabletOrMobile', isTabletOrMobile);
	const [init, setInit] = useState(null);
	useEffect(() => {
		const run = async () => {
			appInit({ ...config, isTabletOrMobile }, defaultExtensions, defaultModes)
				.then(setInit)
				.catch(console.error);
		};

		run();
	}, [config, defaultExtensions, defaultModes, isTabletOrMobile]);

	if (!init) {
		return null;
	}

	// Set above for named export
	commandsManager = init.commandsManager;
	extensionManager = init.extensionManager;
	servicesManager = init.servicesManager;
	serviceProvidersManager = init.serviceProvidersManager;
	hotkeysManager = init.hotkeysManager;

	// Set appConfig
	const appConfigState = init.appConfig;
	const { routerBasename, modes, dataSources, showStudyList } = appConfigState;

	const {
		uiDialogService,
		uiModalService,
		uiViewportDialogService,
		viewportGridService,
		sessionAuthenticationService,
		customizationService,
		uiNotificationService,
		// dicomPrintService,
	} = servicesManager.services;

	const providers = [];
	providers.push(
		[QueryClientProvider, { client: queryClient }],
		[AppConfigProvider, { value: appConfigState }],
		[SessionAuthenticationProvider, { service: sessionAuthenticationService }],
		[I18nextProvider, { i18n }],
		[ThemeWrapper],
		[SystemContextProvider, { commandsManager, extensionManager, hotkeysManager, servicesManager }],
		[ViewportRefsProvider],
		[ViewportGridProvider, { service: viewportGridService }],
		[ViewportDialogProvider, { service: uiViewportDialogService }],
		[DialogProvider, { service: uiDialogService }],
		[ModalProvider, { service: uiModalService, modal: Modal }],
		[SnackbarProvider, { service: uiNotificationService }],
		[DicomPrintProvider, { servicesManager }],
	);

	// Loop through and register each of the service providers registered with the ServiceProvidersManager.
	const providersFromManager = Object.entries(serviceProvidersManager.providers);
	if (providersFromManager.length > 0) {
		providersFromManager.forEach(([serviceName, provider]) => {
			providers.push([provider, { service: servicesManager.services[serviceName], commandsManager }]);
		});
	}

	// Should there be a generic call to init on the extension manager?
	customizationService.init(extensionManager);

	// Use config to create routes
	const appRoutes = createRoutes({
		modes,
		dataSources,
		extensionManager,
		servicesManager,
		commandsManager,
		hotkeysManager,
		routerBasename,
		showStudyList,
	});

	return (
		<Compose components={providers}>
			<BrowserRouter basename={routerBasename}>{appRoutes}</BrowserRouter>
		</Compose>
	);
}

App.propTypes = {
	config: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({
			routerBasename: PropTypes.string.isRequired,
			whiteLabeling: PropTypes.object,
			extensions: PropTypes.array,
		}),
	]).isRequired,
	/* Extensions that are "bundled" or "baked-in" to the application.
	 * These would be provided at build time as part of they entry point. */
	defaultExtensions: PropTypes.array,
	defaultModes: PropTypes.array,
};

export default App;

export { commandsManager, extensionManager };
